import { useMemo } from "react";

import {
    applyChimneysToScope,
    applyFurnaceVentsToScope,
    applyLaborFormulaScopeAliases,
    applyRoofPitchToScope,
    applySkylightsToScope,
    sanitizeVariableName,
} from "../../../utils/formulaScope";
import { ROOF_MEASUREMENT_KEYS } from "../../../constants/roofMeasurements";
import { resolveMeasurementNumericValue } from "../../../utils/measurementAttribute";
import type { LineEdits } from "../types";
import type { MaterialRowItem } from "../utils/materialLineItems";
import { buildMaterialSelectionsScope } from "../utils/materialSelectionsScope";
import { useEstimateContractContextManager } from "./";

const DEFAULT_NUMERIC_SCOPE: Record<string, number> = {
    Area: 0,
    Eave: 0,
    Rake: 0,
    Ridge: 0,
    Hip: 0,
    Valley: 0,
    RollValley: 0,
    HeadWallMetalQty: 0,
    StepFlashingQty: 0,
    CounterFlashingQty: 0,
    BoxVentQty: 0,
    TurbineVentQty: 0,
    RoofVents: 0,
    PlumbingJacksQty: 0,
    SplitBootsQty: 0,
    FurnaceVents: 0,
    BathVentQty: 0,
    DryerVentQty: 0,
    GutterLengthQty: 0,
    DownspoutLengthQty: 0,
    ScreenLength: 0,
    KickoutDiverterQty: 0,
    TearOff: 0,
    LowSlopeSQ: 0,
    IceWaterBarrierSQ: 0,
    EvaporativeCooler: 0,
    BrickChimneys: 0,
    WoodChimneys: 0,
    Chimneys: 0,
    CurbSkylights: 0,
    DeckSkylights: 0,
    Skylights: 0,
    RidgeVentLf: 0,
    SteepSquares13Plus: 0,
    RoofPitch7Square: 0,
    RoofPitch8Square: 0,
    RoofPitch9Square: 0,
    RoofPitch10Square: 0,
    RoofPitch11Square: 0,
    RoofPitch12Square: 0,
};

const DEFAULT_STRING_SCOPE: Record<string, string> = {
    ValleyType: "",
    EaveDripEdge: "",
    RakeDripEdge: "",
    BathVentSize: "",
    DryerVentSize: "",
    CounterFlashingSize: "",
    GutterSize: "",
    DownspoutSize: "",
    RoofColor: "",
    UnderlaymentType: "",
    VentilationType: "",
    GutterGuardType: "",
};

export function useCalculationScope(
    materialsByGroup?: Record<string, MaterialRowItem[]>,
) {
    const { values, initLoading } = useEstimateContractContextManager();

    const scope = useMemo(
        () =>
            buildScope(
                values.measurements,
                values.attributes,
                values.materials,
                materialsByGroup,
            ),
        [
            values.measurements,
            values.attributes,
            values.materials,
            materialsByGroup,
        ],
    );

    return {
        scope,
        loading: initLoading,
    };
}

function buildScope(
    measurements: any,
    attributes: any,
    materialsEdits?: LineEdits,
    materialsByGroup?: Record<string, MaterialRowItem[]>,
) {
    let scope: any = {
        ...DEFAULT_NUMERIC_SCOPE,
        ...DEFAULT_STRING_SCOPE,
    };

    for (const key of ROOF_MEASUREMENT_KEYS) {
        const entry = measurements[key];
        scope[sanitizeVariableName(key)] = resolveMeasurementNumericValue(entry);
    }

    for (const [key, value] of Object.entries(attributes || {})) {
        if (key === "RoofPitch") {
            scope = { ...scope, ...applyRoofPitchToScope(value) };
            continue;
        }

        if (key === "FurnaceVents") {
            scope = { ...scope, ...applyFurnaceVentsToScope(value) };
            continue;
        }

        if (key === "Chimneys") {
            scope = { ...scope, ...applyChimneysToScope(value) };
            continue;
        }

        if (key === "Skylights") {
            scope = { ...scope, ...applySkylightsToScope(value) };
            continue;
        }

        scope[sanitizeVariableName(key)] = value;
    }

    scope = {
        ...scope,
        ...buildMaterialSelectionsScope(materialsEdits, materialsByGroup),
    };

    return applyLaborFormulaScopeAliases(scope);
}
